import { Crosshair, Milestone, RotateCcw, Route, Trash2 } from "lucide-react";
import { useRef, useState } from "react";

import type { PhaseLine, Zone } from "../api/types";
import { flashAttention } from "../components/attention";
import { PropRow } from "../components/panel";
import { Motion, Presence } from "../components/presence";
import { SaveDossierHint } from "../components/SaveDossierHint";
import { Callout } from "../components/Callout";
import { Badge, Button, FloatingPanel } from "../components/ui";
import { k, locale, t, tServer } from "../i18n/i18n";
import { formatDtg } from "../map/dtg";
import { useDeployment } from "../map/units/useDeployment";
import { useLineLabel } from "../map/lineNames";
import { MAX_HOURS, MIN_HOURS, removableLine, removeLastLine, setLineHours, validate, widthContextOf } from "../map/manoeuvre/corridorEdit";
import { useManoeuvre } from "../map/manoeuvre/useManoeuvre";
import { useManoeuvreWrites } from "../map/manoeuvre/useManoeuvreWrites";
import { refuseCorridorWhilePreparing } from "../map/zones/zoneAttention";
import { useOpenZone } from "../map/zones/useZones";
import { useUnitMoves } from "../map/units/useUnitMoves";
import { useUi } from "../state/ui";
import { CardActions, CardFooter, CardTitle } from "./cardParts";
import { CommitInput } from "./GraphicCard";

/** Fiche d'une ligne de coordination : GDH (calculé ou saisi), glissement le long du fuseau, retour à la position
 *  doctrinale, retrait de la LIMA de plus haut rang (docs/14 §2.4). Avec un fuseau personnalisé, le GDH saisi, son
 *  effacement et le retrait passent par le modèle du fuseau (`PUT /corridor`) ; avec le fuseau automatique, la
 *  fiche garde son comportement d'avant et désigne « Modifier le fuseau » (docs/14 §5.4 : GDH saisi en fuseau
 *  utilisateur seulement). Une retouche de la carte en attente de réponse bloque ces écritures : elles partiraient
 *  de la géométrie d'avant le geste. */

function meaning(name: PhaseLine["name"], label: (name: PhaseLine["name"]) => string): string {
  const texts: Record<PhaseLine["name"], string> = {
    L0: t("Ligne de débouché : dispositif ami initial (T1)"),
    L1: t("Dispositif ami en T2"),
    L2: t("Dispositif ami en T3"),
    L3: t("Au-delà de {l2}, de la distance {l1} → {l2}", { l1: label("L1"), l2: label("L2") }),
  };
  return texts[name];
}

// Rotation CENTAC (ChatController.centacLimaLine) : L0 sur la ligne de débouché fixe, L1 à L3 sur les échelons
// de l'ennemi, du plus proche au plus profond.
const CENTAC_MEANING: Record<PhaseLine["name"], string> = {
  L0: k("Ligne de débouché : base de départ du GTIA"),
  L1: k("Échelon de tête ennemi (reconnaissance)"),
  L2: k("Gros de l'ennemi (échelon central)"),
  L3: k("Appui ennemi (mortiers), en arrière : objectif"),
};

/** Pastille d'état unifiée des LIMA (fiche, panneau Manœuvre), réservée à l'exception (R6) : « GDH saisi » ou
 *  « Glissée ». À sa position doctrinale, la ligne n'a pas de pastille (rien : null). */
export function LineStateBadge({ entered, moved }: { entered: boolean; moved: boolean }) {
  if (!entered && !moved) return null;
  return (
    <Badge dot tone="accent">
      {entered ? t("GDH saisi") : t("Glissée")}
    </Badge>
  );
}

/** Désigne la LIMA retirable : sa ligne du panneau Manœuvre s'il est ouvert, sinon son étiquette sur la carte. */
export function flashLimaRow(name: string | null) {
  if (!name) return;
  const row = document.querySelector<HTMLElement>(`[data-lima-row="${name}"]`);
  flashAttention(row ?? document.querySelector<HTMLElement>(`[data-line-tag="${name}"]`), "field");
}

/** Ouvre la vue Manœuvre et fait clignoter « Modifier le fuseau », une fois le panneau apparu. Terrain libre en
 *  préparation (`zone`) : le fuseau ne se retouche qu'une fois le terrain prêt, la carte de progression est désignée. */
export function designateEditCorridor(zone?: Zone | null) {
  if (refuseCorridorWhilePreparing(zone)) return;
  useUi.getState().setView("manoeuvre");
  window.setTimeout(
    () => flashAttention(document.querySelector<HTMLElement>("[data-manoeuvre-edit]"), "button"),
    320,
  );
}

export function LineCard({ name }: { name: PhaseLine["name"] }) {
  const { data } = useDeployment();
  const select = useUi((s) => s.select);
  const notify = useUi((s) => s.notify);
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const params = useUi((s) => s.deploymentParams);
  const { canMove, resetLine, busy } = useUnitMoves();
  const { setCorridor, busy: writes } = useManoeuvreWrites();
  // Retouche de la carte affichée mais pas encore enregistrée (geste, flèches, réponse attendue).
  const gesturePending = useManoeuvre((s) => s.working !== null);
  const writing = writes || gesturePending;
  const editingCorridor = useManoeuvre((s) => s.editing);
  // Terrain libre en préparation : GDH saisi et retrait (retouches du fuseau) désignent la carte de progression.
  const { zone } = useOpenZone();
  const lineLabel = useLineLabel();
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const hoursRef = useRef<HTMLInputElement>(null);
  const corridor = data?.corridor;
  const line = corridor?.lines.find((l) => l.name === name);
  if (!corridor || !line) return null;
  const rotation = data?.centac ?? null;
  // Modèle éditable : fuseau personnalisé, ou fuseau automatique en cours de modification (« Modifier le fuseau ») ;
  // sinon la fiche d'avant. Aucun en CENTAC.
  const model = rotation || !(corridor.custom || editingCorridor) ? null : corridor.model;
  // Fuseau automatique hors édition : la saisie du GDH et le retrait désignent « Modifier le fuseau ».
  const automatic = !rotation && !model && Boolean(corridor.model);
  const modelLine = model?.lines[name];
  const entered = modelLine?.hours !== null && modelLine?.hours !== undefined;
  const moved = line.moved || Boolean(modelLine?.moved);
  const hours = corridor.effective_hours?.[name] ?? line.hours;
  const removable = model ? removableLine(model) : null;
  const editable = Boolean(model && modelLine && canMove);

  const fail = (message: string) => {
    setError(message);
    window.requestAnimationFrame(() => flashAttention(hoursRef.current, "field"));
  };

  const commitHours = (text: string) => {
    if (!model || refuseCorridorWhilePreparing(zone)) return;
    const value = Number(text.trim().replace(",", "."));
    if (!Number.isInteger(value) || value < MIN_HOURS || value > MAX_HOURS) {
      fail(t("Saisissez un nombre entier d'heures, de {min} à {max}.", { min: MIN_HOURS, max: MAX_HOURS }));
      return;
    }
    if (value === hours && !entered) return;
    const next = setLineHours(model, name, value);
    // GDH incohérent (L1 avant L0) : refusé avant l'envoi, avec le message du serveur.
    const check = validate(next, corridor.bounds, model.level, widthContextOf(corridor, data?.level_key));
    if (check.errors.length) {
      fail(tServer(check.errors[0]!));
      return;
    }
    setError(null);
    // Refus du serveur : son message est déjà dans l'avis d'échec ; le champ fautif clignote, sans redire le texte.
    setCorridor
      .mutateAsync({ corridor: next, label: t("GDH de {line}", { line: lineLabel(name) }) })
      .catch(() => window.requestAnimationFrame(() => flashAttention(hoursRef.current, "field")));
  };

  const clearHours = () => {
    if (!model || refuseCorridorWhilePreparing(zone)) return;
    setError(null);
    setCorridor.mutate({ corridor: setLineHours(model, name, null), label: t("retour au GDH calculé de {line}", { line: lineLabel(name) }) });
  };

  // Retrait en fin de liste seulement (§2.4) : la LIMA présente de plus haut rang, jamais L0.
  const remove = () => {
    if (!model || refuseCorridorWhilePreparing(zone)) return;
    if (removable !== name) {
      notify({
        text:
          name === "L0" || removable === null
            ? t("{line} est la ligne de débouché citée par l'ordre", { line: lineLabel("L0") })
            : t("Retirez d'abord {line}", { line: lineLabel(removable) }),
        detail: t("Les LIMA se retirent en partant de la plus haute."),
        tone: "info",
      });
      flashLimaRow(removable);
      return;
    }
    const next = removeLastLine(model);
    if (!next) return;
    setCorridor
      .mutateAsync({ corridor: next, label: t("retrait de {line}", { line: lineLabel(name) }) })
      .then(() => select(null))
      .catch(() => undefined);
  };

  const typedHours = Number(typed.trim());
  const liveHours = Number.isInteger(typedHours) && typedHours >= MIN_HOURS && typedHours <= MAX_HOURS ? typedHours : null;
  const liveDtg = liveHours !== null && params?.start_dtg ? formatDtg(params.start_dtg, liveHours, params.time_zone) : null;

  const removeBlocked = removable !== name || writing;
  const footerActions = (
    <CardFooter>
      <Button variant="secondary" size="sm" onClick={() => requestFlyTo(line.center, 11.5)}>
        <Crosshair size={14} /> {t("Centrer")}
      </Button>
      {line.moved && canMove && (
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => resetLine.mutate(name)}>
          <RotateCcw size={14} /> {t("Revenir à la position doctrinale")}
        </Button>
      )}
      {editable && entered && (
        <Button variant="secondary" size="sm" disabled={writing} onClick={clearHours}>
          <RotateCcw size={14} /> {t("Revenir au GDH calculé")}
        </Button>
      )}
      {editable && (
        // Seule la LIMA de plus haut rang se retire : sur une autre, le clic dit laquelle et la désigne.
        <Button
          variant="danger-ghost"
          size="sm"
          blocked={removeBlocked}
          onBlocked={() => (writing ? undefined : remove())}
          onClick={remove}
          title={removable === name ? t("Retirer {line} du fuseau", { line: lineLabel(name) }) : undefined}
        >
          <Trash2 size={14} /> {t("Retirer la ligne")}
        </Button>
      )}
    </CardFooter>
  );

  return (
    <FloatingPanel
      enter="right"
      width="card"
      tallHeader
      icon={Milestone}
      title={<CardTitle title={lineLabel(name)} subtitle={t("Ligne de coordination")} />}
      actions={<CardActions />}
      footer={footerActions}
    >
      <div className="px-4 py-2">
        <PropRow label={t("État")}>
          {entered || moved ? <LineStateBadge entered={entered} moved={moved} /> : <span className="text-ink-muted">{t("Position doctrinale")}</span>}
        </PropRow>
        <PropRow label={t("Rôle")}>{rotation ? t(CENTAC_MEANING[name]) : meaning(name, lineLabel)}</PropRow>
        {rotation?.lima === name && (
          <PropRow label={t("OPO {n}", { n: rotation.opo })}>{t("Le dispositif ami s'y pose")}</PropRow>
        )}
        <PropRow label={t("GDH")} mono={Boolean(line.dtg)}>
          {line.dtg ? <span className="whitespace-nowrap">{line.dtg}</span> : t("début d'exercice non renseigné")}
        </PropRow>
        {editable ? (
          <PropRow label={t("Échéance")}>
            <span className="flex flex-col items-end">
              <span className="flex items-center gap-1.5">
                <span className="text-ink-muted">{t("Début")} +</span>
                <span className="w-16">
                  <CommitInput
                    value={String(hours)}
                    numeric
                    disabled={writing}
                    onInput={(text) => {
                      setTyped(text);
                      if (error) setError(null);
                    }}
                    onCommit={commitHours}
                    error={error}
                    showError={false}
                    inputRef={hoursRef}
                    ariaLabel={t("Échéance de {line} en heures après le début", { line: lineLabel(name) })}
                  />
                </span>
                <span className="text-ink-muted">h</span>
              </span>
              {error ? (
                <Motion key="error" origin="fade" className="pt-1 text-meta leading-snug text-danger">
                  {error}
                </Motion>
              ) : liveDtg && liveHours !== hours ? (
                <Motion key="live" origin="fade" className="pt-1 text-meta text-ink-muted">
                  {t("GDH résultant :")} <span className="font-mono whitespace-nowrap text-ink-soft">{liveDtg}</span>
                </Motion>
              ) : null}
            </span>
          </PropRow>
        ) : (
          <PropRow label={t("Échéance")}>
            H + {line.hours}
            {line.offset_h !== 0 && (
              <span className="ml-1 text-ink-muted">
                ({line.offset_h > 0 ? "+" : "−"}
                {Math.abs(line.offset_h)} h)
              </span>
            )}
          </PropRow>
        )}
        <PropRow label={t("Glissement")}>
          {line.moved
            ? `${(line.shift_m / 1000).toLocaleString(locale(), { maximumFractionDigits: 1, signDisplay: "always" })} km`
            : "—"}
        </PropRow>
      </div>
      {/* LIMA resserrées faute de profondeur au bout du fuseau : l'avis du serveur, en fondu. */}
      <Presence show={Boolean(line.warning)}>
        <Motion origin="fade" className="px-4 pb-2">
          <Callout tone="warn">{tServer(line.warning ?? "")}</Callout>
        </Motion>
      </Presence>
      <div className="border-t border-line px-4 py-3">
        <Presence show={automatic && canMove}>
          <Motion origin="fade" className="mb-1.5 text-meta leading-snug text-ink-muted">
            {t("Le GDH se saisit et la ligne se retire en modifiant le fuseau.")}{" "}
            <Button variant="link" onClick={() => designateEditCorridor(zone)}>
              <Route size={14} /> {t("Modifier le fuseau")}
            </Button>
          </Motion>
        </Presence>
        {canMove ? (
          <p className="text-meta leading-snug text-ink-muted">
            {t("Faites glisser la ligne le long du fuseau : son GDH se décale de 10 h pour toute la longueur du fuseau.")}
          </p>
        ) : (
          <SaveDossierHint action="faire glisser la ligne" />
        )}
      </div>
    </FloatingPanel>
  );
}
